'use client';

import { c as _c } from "react/compiler-runtime";
import { useAddToWatchlist, useGetMarketField, useRemoveFromWatchlist } from '@morfi/backend-sdk-v2/client';
import { useLocale, useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { HowItWorksInfoButton } from '@/components/how-it-works/how-it-works-info-button';
import { MarketHowItWorksModal } from '@/components/how-it-works/market-how-it-works-modal';
import { OptimizedImage } from '@/components/images/optimized-image';
import type { LiveEventSummary } from '@/components/live/types';
import { CopyableCashtagBadge } from '@/components/market/copyable-cashtag-badge';
import { ThumbWithOrigin } from '@/components/market/identity/identity-marks';
import type { MarketIdentityData } from '@/components/market/identity/market-identity';
import { LiveMarketContext } from '@/components/market/live-market-context';
import { ShareButton } from '@/components/market/share-button';
import { AnimatedBookmark } from '@/components/ui/animated-bookmark';
import { Badge } from '@/components/ui/app-badge';
import { Icon } from '@/components/ui/icon';
import { Award01Icon, Coins01Icon, Loading03Icon, RadioButtonIcon, Rocket01Icon } from '@/components/ui/icon/glyphs';
import { NavLink } from '@/components/ui/navigation-list';
import { capitalizeLeading, resolveProposalCategory } from '@/config/proposal-display';
import { TOAST_IDS } from '@/config/toast-ids';
import { MARKETS_BOOKMARK_TOGGLED } from '@/events/markets';
import { useMinWidthSm } from '@/hooks/ui/use-min-width-sm';
import { useAuthGate } from '@/hooks/use-auth-gate';
import { useAuthenticatedWatchlist } from '@/hooks/use-authenticated-watchlist';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { trackEvent } from '@/lib/analytics';
import { useFormattedCurrency } from '@/lib/currency';
import { formatRelativeTime } from '@/lib/format-relative-time';
import { watchlistKindForStatus } from '@/lib/market/watchlist-kind';
import { showToast } from '@/lib/ui/toast';
import { cn } from '@/lib/ui/utils';
import { useGraduationModalStore } from '@/stores/graduation-modal';
import { useManipulationModalStore } from '@/stores/manipulation-modal';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface MarketHeaderMarket {
  title: string;
  /** Market ticker (e.g. `$MEME`). Rendered as the leading chip in the meta row.
   *  Null/absent hides the chip. */
  ticker?: string | null;
  imageUrl: string | null;
  /**
   * NSFW flag for the cover (`imageUrl`). Sourced from the SDK
   * `coverImageNsfw` field on the market detail response (spec 071).
   * Defaults `false` at the callsite when the parent has a backend
   * version that predates the field.
   */
  imageNsfw?: boolean;
  category: string | null;
  createdAt: string;
  /** D6-scaled USDC string (same field as the markets list payload). */
  volumeTotal: string;
}
interface MarketHeaderSharedProps {
  /** Influencer / Featured origin mark rendered on the cover's corner. */
  marketType?: MarketIdentityData['marketType'];
  marketPubkey: string;
  /**
   * Status the page presents. The detail page holds `trading` while a
   * resolution lands, so the bookmark keeps its watchlist until the page swaps.
   */
  presentedStatus?: string;
  className?: string;
  /** Relative path the share button points at. Defaults to
   *  `/markets/{marketPubkey}`; the winners route passes `/winners/{mint}`. */
  canonicalPath?: string;
}
type MarketHeaderProps = MarketHeaderSharedProps & ({
  market: MarketHeaderMarket & {
    kind?: 'consensus';
  };
  liveEvent?: undefined;
} | {
  market: MarketHeaderMarket & {
    kind: 'live';
  };
  liveEvent: LiveEventSummary;
});

// ---------------------------------------------------------------------------
// Status Badge
// ---------------------------------------------------------------------------

const ICON_CLASS = 'size-3 shrink-0';
const ANIMATED_ICON_CLASS = `${ICON_CLASS} animate-spin motion-reduce:animate-none`;

/**
 * Lifecycle hue → outcome-token classes. The semantic outcome-token system in
 * globals.css handles light/dark mode automatically (light: fill=100/text=700;
 * dark: fill=900/text=400), so no `dark:` modifier is ever needed here.
 */
const HUE_CLASSES = {
  red: 'bg-outcome-red-fill text-outcome-red-text',
  orange: 'bg-outcome-orange-fill text-outcome-orange-text',
  amber: 'bg-outcome-amber-fill text-outcome-amber-text',
  yellow: 'bg-outcome-yellow-fill text-outcome-yellow-text',
  green: 'bg-outcome-green-fill text-outcome-green-text',
  emerald: 'bg-outcome-emerald-fill text-outcome-emerald-text',
  teal: 'bg-outcome-teal-fill text-outcome-teal-text',
  sky: 'bg-outcome-sky-fill text-outcome-sky-text'
} as const;
interface StatusConfig {
  labelKey: string;
  className: string;
  icon: React.ReactNode;
}
function getStatusConfig(status: string): StatusConfig | null {
  switch (status) {
    case 'created':
      return {
        labelKey: 'status.created',
        className: HUE_CLASSES.sky,
        icon: <Icon icon={Loading03Icon} className={ANIMATED_ICON_CLASS} />
      };
    case 'backing':
      return {
        labelKey: 'status.backing',
        className: HUE_CLASSES.emerald,
        icon: <Icon icon={Coins01Icon} className={ICON_CLASS} />
      };
    case 'trading':
      return {
        labelKey: 'status.trading',
        className: HUE_CLASSES.green,
        icon: <Icon icon={RadioButtonIcon} className={ICON_CLASS} />
      };
    case 'resolved':
      return {
        labelKey: 'status.resolved',
        className: HUE_CLASSES.green,
        icon: <Icon icon={Award01Icon} className={ICON_CLASS} />
      };
    case 'graduated':
      return {
        labelKey: 'status.graduated',
        className: HUE_CLASSES.teal,
        icon: <Icon icon={Rocket01Icon} className={ICON_CLASS} />
      };
    default:
      return null;
  }
}
export function StatusBadge(t0) {
  const $ = _c(21);
  const {
    status,
    winnerName: t1,
    className
  } = t0;
  const winnerName = t1 === undefined ? null : t1;
  const t = useTranslations("market");
  let T0;
  let config;
  let label;
  let t2;
  let t3;
  let t4;
  if ($[0] !== className || $[1] !== status || $[2] !== t || $[3] !== winnerName) {
    t4 = Symbol.for("react.early_return_sentinel");
    bb0: {
      config = getStatusConfig(status);
      if (config === null) {
        let t5;
        if ($[10] !== className) {
          t5 = cn("bg-background-tertiary text-foreground-secondary", className);
          $[10] = className;
          $[11] = t5;
        } else {
          t5 = $[11];
        }
        let t6;
        if ($[12] !== status || $[13] !== t5) {
          t6 = <Badge variant="secondary" className={t5}>{status}</Badge>;
          $[12] = status;
          $[13] = t5;
          $[14] = t6;
        } else {
          t6 = $[14];
        }
        t4 = t6;
        break bb0;
      }
      label = status === "resolved" && winnerName !== null ? t("header.resolvedWinner", {
        winner: winnerName
      }) : t(config.labelKey as Parameters<typeof t>[0]);
      T0 = Badge;
      t2 = "secondary";
      t3 = cn("gap-1", config.className, className);
    }
    $[0] = className;
    $[1] = status;
    $[2] = t;
    $[3] = winnerName;
    $[4] = T0;
    $[5] = config;
    $[6] = label;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    T0 = $[4];
    config = $[5];
    label = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
  }
  if (t4 !== Symbol.for("react.early_return_sentinel")) {
    return t4;
  }
  let t5;
  if ($[15] !== T0 || $[16] !== config.icon || $[17] !== label || $[18] !== t2 || $[19] !== t3) {
    t5 = <T0 variant={t2} className={t3}>{config.icon}{label}</T0>;
    $[15] = T0;
    $[16] = config.icon;
    $[17] = label;
    $[18] = t2;
    $[19] = t3;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  return t5;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const TAG_TEXT_CLASS = 'text-foreground-tertiary';
const TAG_LINK_CLASS = 'text-foreground-tertiary hover:text-foreground-brand';
export { LiveMarketContext } from '@/components/market/live-market-context';
export function MarketHeader(t0) {
  const $ = _c(136);
  const {
    marketType,
    market,
    marketPubkey,
    presentedStatus,
    className,
    canonicalPath,
    liveEvent
  } = t0;
  const locale = useLocale();
  const t = useTranslations("market");
  const tMarketHeader = useTranslations("market.header");
  const tWatch = useTranslations("common.watchlist");
  const tCategories = useTranslations("categories");
  const {
    formatD6
  } = useFormattedCurrency();
  const tTimeIntl = useTranslations("time");
  let t1;
  if ($[0] !== tTimeIntl) {
    t1 = (key, params) => tTimeIntl(key as Parameters<typeof tTimeIntl>[0], params);
    $[0] = tTimeIntl;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const tTime = t1;
  let t2;
  if ($[2] !== marketPubkey) {
    t2 = {
      pubkey: marketPubkey
    };
    $[2] = marketPubkey;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const liveStatus = useGetMarketField(t2, "status");
  const t3 = presentedStatus ?? liveStatus;
  let t4;
  if ($[4] !== t3) {
    t4 = watchlistKindForStatus(t3);
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const watchKind = t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = ["pubkeys"];
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== watchKind) {
    t6 = {
      kind: watchKind,
      fields: t5
    };
    $[7] = watchKind;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const {
    data: watchlist
  } = useAuthenticatedWatchlist(t6);
  const {
    ensureAuthenticated
  } = useAuthGate();
  const addToWatchlist = useAddToWatchlist();
  const removeFromWatchlist = useRemoveFromWatchlist();
  let t7;
  if ($[9] !== marketPubkey || $[10] !== watchlist?.pubkeys) {
    t7 = watchlist?.pubkeys?.includes(marketPubkey) ?? false;
    $[9] = marketPubkey;
    $[10] = watchlist?.pubkeys;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const isBookmarked = t7;
  const isAtLeastSm = useMinWidthSm();
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  const openGraduation = useGraduationModalStore(_temp);
  const openManipulation = useManipulationModalStore(_temp2);
  let t8;
  if ($[12] !== addToWatchlist || $[13] !== ensureAuthenticated || $[14] !== isBookmarked || $[15] !== marketPubkey || $[16] !== removeFromWatchlist || $[17] !== tWatch || $[18] !== watchKind) {
    t8 = function handleBookmarkToggle() {
      if (!ensureAuthenticated()) {
        return;
      }
      if (isBookmarked) {
        removeFromWatchlist.mutate({
          kind: watchKind,
          pubkey: marketPubkey
        });
      } else {
        addToWatchlist.mutate({
          kind: watchKind,
          pubkey: marketPubkey
        });
      }
      showToast({
        id: isBookmarked ? TOAST_IDS.WATCHLIST_REMOVED : TOAST_IDS.WATCHLIST_SAVED,
        message: isBookmarked ? tWatch("removed") : tWatch("added"),
        variant: "success"
      });
      trackEvent(MARKETS_BOOKMARK_TOGGLED.name, {
        marketPubkey,
        saved: !isBookmarked
      });
    };
    $[12] = addToWatchlist;
    $[13] = ensureAuthenticated;
    $[14] = isBookmarked;
    $[15] = marketPubkey;
    $[16] = removeFromWatchlist;
    $[17] = tWatch;
    $[18] = watchKind;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const handleBookmarkToggle = t8;
  const hasCategory = market.category !== null && market.category.length > 0;
  const hasAge = market.createdAt.length > 0;
  const hasTicker = market.ticker !== null && market.ticker !== undefined && market.ticker !== "";
  const resolvedProposalCategory = market.category !== null && market.category.length > 0 ? resolveProposalCategory(market.category) : null;
  const categoryHref = hasCategory && market.category !== null ? `/markets?category=${encodeURIComponent(resolvedProposalCategory !== null ? resolvedProposalCategory.name : market.category)}` : undefined;
  const categoryIntent = useIntentRoutePrefetch(categoryHref);
  let t9;
  if ($[20] !== hasTicker || $[21] !== liveEvent || $[22] !== market.kind || $[23] !== market.ticker) {
    t9 = market.kind === "live" && liveEvent !== undefined ? [<LiveMarketContext key="live-context" event={liveEvent} ticker={market.ticker} />] : hasTicker ? [<CopyableCashtagBadge key="ticker" ticker={market.ticker} />] : [];
    $[20] = hasTicker;
    $[21] = liveEvent;
    $[22] = market.kind;
    $[23] = market.ticker;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const t10 = hasCategory && market.category !== null ? [<NavLink key="category" href={categoryHref ?? "/markets"} prefetch={false} {...categoryIntent} className={TAG_LINK_CLASS}>{resolvedProposalCategory !== null ? tCategories(resolvedProposalCategory.id as keyof IntlMessages['categories']) : capitalizeLeading(market.category)}</NavLink>] : [];
  let t11;
  if ($[25] !== hasAge || $[26] !== isAtLeastSm || $[27] !== locale || $[28] !== market.createdAt || $[29] !== tTime) {
    t11 = hasAge ? [<span key="age" className={TAG_TEXT_CLASS}>{formatRelativeTime(market.createdAt, tTime, {
        locale,
        style: isAtLeastSm ? "long" : "short"
      })}</span>] : [];
    $[25] = hasAge;
    $[26] = isAtLeastSm;
    $[27] = locale;
    $[28] = market.createdAt;
    $[29] = tTime;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = cn(TAG_TEXT_CLASS, "tabular-nums");
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== formatD6 || $[33] !== market.volumeTotal) {
    t13 = formatD6(market.volumeTotal, "compact");
    $[32] = formatD6;
    $[33] = market.volumeTotal;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== tMarketHeader) {
    t14 = tMarketHeader("tradedLabel");
    $[35] = tMarketHeader;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== t13 || $[38] !== t14) {
    t15 = <span key="volumeTotal" className={t12}>{t13} {t14}</span>;
    $[37] = t13;
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== t10 || $[41] !== t11 || $[42] !== t15 || $[43] !== t9) {
    t16 = [...t9, ...t10, ...t11, t15];
    $[40] = t10;
    $[41] = t11;
    $[42] = t15;
    $[43] = t9;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  const tagNodes = t16;
  let t17;
  if ($[45] !== className) {
    t17 = cn("flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-3", className);
    $[45] = className;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== market.category || $[48] !== market.imageNsfw || $[49] !== market.imageUrl || $[50] !== market.title || $[51] !== marketPubkey || $[52] !== marketType) {
    t18 = market.imageUrl !== null && market.imageUrl.length > 0 ? <ThumbWithOrigin marketType={marketType}><OptimizedImage variant="thumbnail" entity={{
        kind: "market",
        pubkey: marketPubkey,
        category: String(market.category ?? "").toLowerCase(),
        coverImageUrl: market.imageUrl,
        nsfw: market.imageNsfw ?? false
      }} alt={market.title} size="size-16" rounded="rounded-overlay-item" /></ThumbWithOrigin> : null;
    $[47] = market.category;
    $[48] = market.imageNsfw;
    $[49] = market.imageUrl;
    $[50] = market.title;
    $[51] = marketPubkey;
    $[52] = marketType;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== market.title) {
    t19 = <h1 className="min-w-0 flex-1 text-heading-lg leading-tight">{market.title}</h1>;
    $[54] = market.title;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  let t20;
  if ($[56] !== t18 || $[57] !== t19) {
    t20 = <div className="flex items-center gap-3 sm:hidden">{t18}{t19}</div>;
    $[56] = t18;
    $[57] = t19;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== market.category || $[60] !== market.imageNsfw || $[61] !== market.imageUrl || $[62] !== market.title || $[63] !== marketPubkey || $[64] !== marketType) {
    t21 = market.imageUrl !== null && market.imageUrl.length > 0 ? <div className="hidden sm:block"><ThumbWithOrigin marketType={marketType}><OptimizedImage variant="thumbnail" entity={{
          kind: "market",
          pubkey: marketPubkey,
          category: String(market.category ?? "").toLowerCase(),
          coverImageUrl: market.imageUrl,
          nsfw: market.imageNsfw ?? false
        }} alt={market.title} size="size-24" className="block" rounded="rounded-card" /></ThumbWithOrigin></div> : null;
    $[59] = market.category;
    $[60] = market.imageNsfw;
    $[61] = market.imageUrl;
    $[62] = market.title;
    $[63] = marketPubkey;
    $[64] = marketType;
    $[65] = t21;
  } else {
    t21 = $[65];
  }
  let t22;
  if ($[66] !== market.title) {
    t22 = <h1 className="hidden min-w-0 text-heading-lg leading-tight sm:block">{market.title}</h1>;
    $[66] = market.title;
    $[67] = t22;
  } else {
    t22 = $[67];
  }
  let t23;
  if ($[68] !== tagNodes) {
    t23 = <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">{tagNodes.flatMap((node, i) => {
        if (i === 0) {
          return [node];
        }
        const prevKey = (tagNodes[i - 1] as {
          key?: string | null;
        }).key ?? `idx-${i - 1}`;
        return [<span key={`sep-after-${prevKey}`} aria-hidden="true" className={TAG_TEXT_CLASS}>·</span>, node];
      })}</div>;
    $[68] = tagNodes;
    $[69] = t23;
  } else {
    t23 = $[69];
  }
  let t24;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => setHowItWorksOpen(true);
    $[70] = t24;
  } else {
    t24 = $[70];
  }
  let t25;
  if ($[71] !== t) {
    t25 = t("howMarketsWorkLabel");
    $[71] = t;
    $[72] = t25;
  } else {
    t25 = $[72];
  }
  let t26;
  if ($[73] !== t25) {
    t26 = <HowItWorksInfoButton onClick={t24} ariaLabel={t25} />;
    $[73] = t25;
    $[74] = t26;
  } else {
    t26 = $[74];
  }
  let t27;
  if ($[75] !== t) {
    t27 = t("bookmarkLabel");
    $[75] = t;
    $[76] = t27;
  } else {
    t27 = $[76];
  }
  let t28;
  if ($[77] !== handleBookmarkToggle || $[78] !== isBookmarked || $[79] !== t27) {
    t28 = <AnimatedBookmark isSaved={isBookmarked} onClick={handleBookmarkToggle} ariaLabel={t27} />;
    $[77] = handleBookmarkToggle;
    $[78] = isBookmarked;
    $[79] = t27;
    $[80] = t28;
  } else {
    t28 = $[80];
  }
  let t29;
  if ($[81] !== canonicalPath) {
    t29 = canonicalPath !== undefined ? {
      canonicalPath
    } : {};
    $[81] = canonicalPath;
    $[82] = t29;
  } else {
    t29 = $[82];
  }
  let t30;
  if ($[83] !== market.title || $[84] !== marketPubkey || $[85] !== t29) {
    t30 = <ShareButton marketPubkey={marketPubkey} marketTitle={market.title} {...t29} />;
    $[83] = market.title;
    $[84] = marketPubkey;
    $[85] = t29;
    $[86] = t30;
  } else {
    t30 = $[86];
  }
  let t31;
  if ($[87] !== t26 || $[88] !== t28 || $[89] !== t30) {
    t31 = <div className="flex shrink-0 items-center gap-0 sm:hidden">{t26}{t28}{t30}</div>;
    $[87] = t26;
    $[88] = t28;
    $[89] = t30;
    $[90] = t31;
  } else {
    t31 = $[90];
  }
  let t32;
  if ($[91] !== t23 || $[92] !== t31) {
    t32 = <div className="flex w-full max-w-full flex-wrap items-center justify-between gap-x-2 gap-y-1 text-body">{t23}{t31}</div>;
    $[91] = t23;
    $[92] = t31;
    $[93] = t32;
  } else {
    t32 = $[93];
  }
  let t33;
  if ($[94] !== t22 || $[95] !== t32) {
    t33 = <div className="flex min-w-0 flex-1 flex-col gap-1">{t22}{t32}</div>;
    $[94] = t22;
    $[95] = t32;
    $[96] = t33;
  } else {
    t33 = $[96];
  }
  let t34;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = () => setHowItWorksOpen(true);
    $[97] = t34;
  } else {
    t34 = $[97];
  }
  let t35;
  if ($[98] !== t) {
    t35 = t("howMarketsWorkLabel");
    $[98] = t;
    $[99] = t35;
  } else {
    t35 = $[99];
  }
  let t36;
  if ($[100] !== t35) {
    t36 = <HowItWorksInfoButton onClick={t34} ariaLabel={t35} />;
    $[100] = t35;
    $[101] = t36;
  } else {
    t36 = $[101];
  }
  let t37;
  if ($[102] !== t) {
    t37 = t("bookmarkLabel");
    $[102] = t;
    $[103] = t37;
  } else {
    t37 = $[103];
  }
  let t38;
  if ($[104] !== handleBookmarkToggle || $[105] !== isBookmarked || $[106] !== t37) {
    t38 = <AnimatedBookmark isSaved={isBookmarked} onClick={handleBookmarkToggle} ariaLabel={t37} />;
    $[104] = handleBookmarkToggle;
    $[105] = isBookmarked;
    $[106] = t37;
    $[107] = t38;
  } else {
    t38 = $[107];
  }
  let t39;
  if ($[108] !== canonicalPath) {
    t39 = canonicalPath !== undefined ? {
      canonicalPath
    } : {};
    $[108] = canonicalPath;
    $[109] = t39;
  } else {
    t39 = $[109];
  }
  let t40;
  if ($[110] !== market.title || $[111] !== marketPubkey || $[112] !== t39) {
    t40 = <ShareButton marketPubkey={marketPubkey} marketTitle={market.title} {...t39} />;
    $[110] = market.title;
    $[111] = marketPubkey;
    $[112] = t39;
    $[113] = t40;
  } else {
    t40 = $[113];
  }
  let t41;
  if ($[114] !== t36 || $[115] !== t38 || $[116] !== t40) {
    t41 = <div className="hidden shrink-0 items-center gap-0 sm:flex">{t36}{t38}{t40}</div>;
    $[114] = t36;
    $[115] = t38;
    $[116] = t40;
    $[117] = t41;
  } else {
    t41 = $[117];
  }
  let t42;
  if ($[118] !== t17 || $[119] !== t20 || $[120] !== t21 || $[121] !== t33 || $[122] !== t41) {
    t42 = <div className={t17}>{t20}{t21}{t33}{t41}</div>;
    $[118] = t17;
    $[119] = t20;
    $[120] = t21;
    $[121] = t33;
    $[122] = t41;
    $[123] = t42;
  } else {
    t42 = $[123];
  }
  let t43;
  if ($[124] !== openGraduation) {
    t43 = () => {
      setHowItWorksOpen(false);
      openGraduation(true);
    };
    $[124] = openGraduation;
    $[125] = t43;
  } else {
    t43 = $[125];
  }
  let t44;
  if ($[126] !== openManipulation) {
    t44 = () => {
      setHowItWorksOpen(false);
      openManipulation(true);
    };
    $[126] = openManipulation;
    $[127] = t44;
  } else {
    t44 = $[127];
  }
  let t45;
  if ($[128] !== howItWorksOpen || $[129] !== marketPubkey || $[130] !== t43 || $[131] !== t44) {
    t45 = <MarketHowItWorksModal marketPubkey={marketPubkey} open={howItWorksOpen} onOpenChange={setHowItWorksOpen} onWhatIsGraduation={t43} onPreventManipulation={t44} />;
    $[128] = howItWorksOpen;
    $[129] = marketPubkey;
    $[130] = t43;
    $[131] = t44;
    $[132] = t45;
  } else {
    t45 = $[132];
  }
  let t46;
  if ($[133] !== t42 || $[134] !== t45) {
    t46 = <div>{t42}{t45}</div>;
    $[133] = t42;
    $[134] = t45;
    $[135] = t46;
  } else {
    t46 = $[135];
  }
  return t46;
}
function _temp2(s_0) {
  return s_0.setOpen;
}
function _temp(s) {
  return s.setOpen;
}